import { getFeatureFlag } from "./featureFlagActionCreator";
import { useAppStateFeatureFlagSelector } from "./featureFlagSlice";
import { useAuthSelector } from "../auth/authSlice";

// Custom react hook to return a specific feature flag by name.
// Returns undefined both when the flag is absent and when the flags have not
// settled yet — pair it with useFeatureFlagsSettled before treating that as "off".
export const useFeatureFlag = (name: string) => {
    const { featureFlags } = useAppStateFeatureFlagSelector(state => state);
    const getFlag = getFeatureFlag(name);
    const flag = getFlag(featureFlags);

    return flag;
};

// True once a /featureflags request for the *current* user has settled.
//
// Deliberately not just "a fetch finished": the endpoint is identity-scoped, and
// App.tsx fires it once before auth resolves and again afterwards. A signed-in
// user whose overrides differ from the global values would otherwise be judged
// against the anonymous response, which is the same class of bug as reading an
// unfetched flag as "off".
//
// Consequence worth knowing: this returns false again briefly while the flags
// for a newly signed-in user are in flight, so gated routes show their loader
// during that window rather than acting on the previous user's flags.
export const useFeatureFlagsSettled = () => {
    const settled = useAppStateFeatureFlagSelector(state => state.featureFlagsLoaded);
    const flagsUserId = useAppStateFeatureFlagSelector(state => state.featureFlagsUserId);
    const authUserId = useAuthSelector(state => state.authUser?.Id ?? null);

    return settled && flagsUserId === authUserId;
};

// True when the last settled flags request failed. Callers that gate access on a
// flag should surface this rather than treating an unknown flag as "off" — the
// two are not the same, and conflating them redirects people off a page they
// asked for because of one dropped request.
export const useFeatureFlagsErrored = () =>
    useAppStateFeatureFlagSelector(state => state.featureFlagsError);
